import { useRef, useEffect, useState } from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { compose } from '@reduxjs/toolkit';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Button from 'core/buttons/Button';
import TextButton from 'core/buttons/TextButton';
import FormContainer from 'core/forms/FormContainer';
import FormCheckbox from 'hook-form-controls/FormCheckbox';
import FormSelectPartner from 'hook-form-controls/FormSelectPartner';
import ValidationErrorMessages from 'core/errors/ValidationErrorMessages';

import Auth from 'utils/Auth';
import app from 'app/config/app';
import YupPassword from 'utils/YupPassword';
import {
  createPartnerFundingSource,
  updatePartnerFundingSource,
} from 'app/features/partner-funding-sources/partnerFundingSourceSaga';
import FormSelectFundingSource from 'hook-form-controls/FormSelectFundingSource';
import FormSelectPartnerCountry from 'hook-form-controls/FormSelectPartnerCountry';

YupPassword(Yup);

const partnerFundingSourceSchema = Yup.object().shape({
  partner_id: Yup.string().required('Select a partner'),
  country: Yup.string().required('Select a country'),
  funding_source: Yup.string().required('Funding Source is required'),
  is_active: Yup.boolean().required(),
});

function PartnerFundingSourceForm({ isAddMode, partnerFundingSource, retries, isCreated }) {
  const submitButtonRef = useRef(null);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const methods = useForm({
    defaultValues: {
      ...(isAddMode ? { is_active: true } : {}),
      ...(partnerFundingSource ? partnerFundingSource : {}),
    },
    resolver: yupResolver(partnerFundingSourceSchema),
    mode: 'onChange',
  });

  const {
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
  } = methods;

  const partnerId = watch('partner_id');

  const onSubmit = async (data) => {
    dispatch(
      isAddMode
        ? createPartnerFundingSource(data)
        : updatePartnerFundingSource({ ...data, id: partnerFundingSource.id }),
    );
  };

  useEffect(() => {
    if (isAddMode) setValue('country', '');
  }, [partnerId]);

  useEffect(() => {
    if (retries > 0) submitButtonRef.current.click();
  }, [retries]);

  useEffect(() => {
    if (isCreated) reset({ partner_id: getValues('partner_id') });
  }, [isCreated]);

  return (
    <FormContainer>
      {app.isDevelopmentMode() && <ValidationErrorMessages errors={errors} />}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item sm={12}>
              <FormSelectPartner
                id="select-partner"
                name="partner_id"
                label="Select Partner"
                error={!!errors?.partner_id}
                helperText={errors?.partner_id?.message ?? ''}
                // disabled={isAddMode}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormSelectPartnerCountry name="country" label="Country" partnerId={partnerId} />
            </Grid>
            <Grid item sm={12} md={6}>
              <FormSelectFundingSource
                id="select-funding-source"
                name="funding_source"
                label="Funding Source"
                options={[]}
              />
            </Grid>
            <Grid item sm={12}>
              <FormCheckbox name="is_active" label="Is Active ?" />
            </Grid>
            <Grid item sm={12} md={12}>
              <Box display="flex" justifyContent="space-between">
                <TextButton type="button" color="error" onClick={() => navigate(-1)}>
                  Cancel
                </TextButton>
                <Button
                  ref={submitButtonRef}
                  type="submit"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                >
                  {isAddMode
                    ? 'Create New Partner Funding Source'
                    : 'Update Partner Funding Source'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </FormContainer>
  );
}

export default compose(connect(mapStateToProps))(PartnerFundingSourceForm);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    retries: ownProps.isAddMode
      ? state.partnerFundingSources.create.retries
      : state.partnerFundingSources.update.retries,
    isCreated: state.partnerFundingSources.create.isCreated,
  };
}

PartnerFundingSourceForm.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  partnerFundingSource: PropTypes.object,
  retries: PropTypes.number,
  isCreated: PropTypes.bool,
};